import { get_token } from "@/router";

let realtime_socket = null;
let reconnect_timer = null;
let reconnect_delay_ms = 1000;
let reconnect_attempts = 0;
let manual_disconnect = false;
let auth_checker = () => true;
const MAX_RECONNECT_DELAY_MS = 30000;
const SOCKET_CONNECTED_EVENT = "realtime:connected";
const SOCKET_DISCONNECTED_EVENT = "realtime:disconnected";

function get_socket_url() {
  const env_url = (process.env.VUE_APP_REALTIME_WS_URL || "").trim();
  return env_url;
}

function build_socket_urls(base_url, token) {
  const separator = base_url.includes("?") ? "&" : "?";
  const safe_socket_url = `${base_url}${separator}token=***`;
  const socket_url = `${base_url}${separator}token=${encodeURIComponent(token)}`;
  return {
    socket_url,
    safe_socket_url,
  };
}

function clear_reconnect_timer() {
  if (!reconnect_timer) return;
  clearTimeout(reconnect_timer);
  reconnect_timer = null;
}

function can_connect() {
  const url = get_socket_url();
  return Boolean(url) && auth_checker();
}

function socket_is_active(socket) {
  if (!socket) return false;
  return socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING;
}

function should_reuse_existing_socket(force_reconnect, existing_socket) {
  if (force_reconnect) return false;
  return socket_is_active(existing_socket);
}

function dispatch_socket_event(event_name, detail = {}) {
  window.dispatchEvent(
    new CustomEvent(event_name, {
      detail,
    })
  );
}

function emit_realtime_event(payload) {
  window.dispatchEvent(
    new CustomEvent("realtime:event", {
      detail: payload,
    })
  );

  // Report/portal-management realtime channel (keeps report handlers separate from event-management handlers)
  if (payload?.module === "portal_management") {
    window.dispatchEvent(
      new CustomEvent("realtime:report", {
        detail: payload,
      })
    );
  }
}

function get_next_reconnect_delay(base_delay_ms) {
  const safe_base_delay = Math.max(1000, Number(base_delay_ms) || 1000);
  const delay = safe_base_delay * (2 ** reconnect_attempts);
  return Math.min(delay, MAX_RECONNECT_DELAY_MS);
}

function register_socket_handlers(socket, socket_url, safe_socket_url, reconnect_delay_ms) {
  socket.onopen = () => {
    clear_reconnect_timer();
    reconnect_attempts = 0;
    dispatch_socket_event(SOCKET_CONNECTED_EVENT, {
      socket_url: safe_socket_url,
      reconnected: socket.__was_reconnect === true,
    });
  };

  socket.onmessage = (event) => {
    console.log("real time raw data",event.data);
    
    let payload;
    try {
      payload = JSON.parse(event.data);
    } catch (error) {
      console.error("[realtime] Invalid WebSocket message:", event.data);
      return;
    }
    console.log("realtime payload");
    emit_realtime_event(payload);
  };

  socket.onerror = (error) => {
    console.error("[realtime] WebSocket error:", error);
  };

  socket.onclose = () => {
    console.log("[realtime] WebSocket disconnected");
    clear_reconnect_timer();
    realtime_socket = null;

    dispatch_socket_event(SOCKET_DISCONNECTED_EVENT, {
      socket_url: safe_socket_url,
      manual: manual_disconnect,
    });

    if (manual_disconnect || !can_connect()) {
      manual_disconnect = false;
      reconnect_attempts = 0;
      return;
    }

    const next_delay_ms = get_next_reconnect_delay(reconnect_delay_ms);
    reconnect_attempts += 1;
    reconnect_timer = setTimeout(() => {
      connect_realtime_socket({ delay_ms: reconnect_delay_ms, is_reconnect: true });
    }, next_delay_ms);
  };
}

export function set_realtime_socket_auth_checker(checker) {
  auth_checker = typeof checker === "function" ? checker : () => true;
}

export function disconnect_realtime_socket() {
  clear_reconnect_timer();
  reconnect_attempts = 0;

  if (!realtime_socket) return;

  manual_disconnect = true;
  realtime_socket.close();
}

export function connect_realtime_socket(options = {}) {
  const delay_ms = options.delay_ms ?? reconnect_delay_ms;
  const force = options.force ?? false;
  const is_reconnect = options.is_reconnect ?? false;

  reconnect_delay_ms = delay_ms;

  if (!can_connect()) {
    disconnect_realtime_socket();
    return null;
  }

  if (should_reuse_existing_socket(force, realtime_socket)) {
    return realtime_socket;
  }

  clear_reconnect_timer();
  manual_disconnect = false;

  const token = get_token();


  if (!token) {
    console.warn("[realtime] No token found. WebSocket not opened.");
    return null;
  }

  const base_url = get_socket_url();
  const { socket_url, safe_socket_url } = build_socket_urls(base_url, token);


  realtime_socket = new WebSocket(socket_url);
  realtime_socket.__was_reconnect = is_reconnect;
  register_socket_handlers(realtime_socket, socket_url, safe_socket_url, delay_ms);

  return realtime_socket;
}

export function get_realtime_socket() {
  return realtime_socket;
}
